import axios from "axios";

// Configurar axios para incluir credenciales en las solicitudes
axios.defaults.withCredentials = true;

// --- Updated: API calls now use relative paths to target the app's own backend proxy ---
const API_BASE_URL = "/api"; // Changed from http://localhost:8005

// --- Axios Response Interceptor for 401 Unauthorized ---
axios.interceptors.response.use(
  (response) => response, // Simply return the response if it's successful
  (error) => {
    if (error.response && error.response.status === 401) {
      // Check if the error is from a session status check to avoid redirect loops if that's the first call
      // Or if we are already on the root page
      if (error.config.url !== `${API_BASE_URL}/session` && window.location.pathname !== "/") {
        console.warn("Session expired or unauthorized. Redirecting to login page.");
        // Perform a hard redirect to the root page (CodeValidationPage)
        // This will also effectively reset the client-side session context
        window.location.href = "/";
      }
    }
    return Promise.reject(error); // Propagate the error so it can be handled by the calling code if needed
  }
);
// --- End of Axios Interceptor ---

// Interfaz para la respuesta de validación de código
interface ValidateCodeResponse {
  success: boolean;
  message: string;
  houseId?: string;
  tooEarly?: boolean;
}

// Interfaz para la respuesta de validación de geolocalización
interface GeoValidateResponse {
  success: boolean;
  distance?: number;
  message?: string;
}

// Interfaz para la respuesta de apertura de puerta (original)
interface OpenDoorResponse {
  success: boolean;
  message: string;
}

// Interfaz para la respuesta del estado de sesión
interface SessionResponse {
  validated: boolean;
  geoValidated: boolean;
  houseId: string | null;
}

// --- New Interfaces based on requirements ---
interface GetInfoPayload {
  house_id: string;
  language: string;
  seqno?: number;
}

interface InfoData {
  house_id: number;
  seqno: number;
  rto: boolean;
  maxtime: number;
  message: string;
  nextSeqno: number;
  photo: string;
  homeAssistant?: {
    status: number;
    message: string;
  };
}

// Updated to include code, longitude, and latitude parameters
interface OpenDoorApiPayload {
  house_id: string | null; // Modificado para aceptar null
  language: string;
  seqno: number;
  code?: string;       // Added: code parameter from /validate
  longitude?: number;  // Added: longitude parameter from /geoValidate
  latitude?: number;   // Added: latitude parameter from /geoValidate
}

// Modificado para extender InfoData y mantener compatibilidad con OpenApiResponse
interface OpenDoorApiResponse extends InfoData {
  success?: boolean;
  // message ya está definido como string requerido en InfoData, no lo redefinimos aquí
}
// --- End of New Interfaces ---

// Función para validar el código
export const validateCode = async (code: string): Promise<ValidateCodeResponse> => {
  try {
    const response = await axios.post(`${API_BASE_URL}/validate`, { code });
    return response.data;
  } catch (error: any) {
    // The interceptor will handle 401, but other errors might still be processed here
    if (error.response && error.response.data && error.response.status !== 401) {
      if (error.response.status === 425) {
        return { ...error.response.data, tooEarly: true };
      }
      return error.response.data;
    }
    // If it's a 401, the interceptor has already acted or will act.
    // For other errors (network, etc.) or if no specific data, return a generic error.
    if (error.response?.status !== 401) {
        return {
            success: false,
            message: error.response?.data?.message || "Error de conexión con el servidor"
        };
    }
    // For 401, we let the interceptor handle the redirect, and the promise will be rejected.
    // To avoid unhandled promise rejection in components if they don't have a .catch for this specific case after redirect:
    // we can return a dummy error or let it reject. Rejecting is cleaner.
    return Promise.reject(error);
  }
};

// Función para validar la geolocalización
export const validateGeoLocation = async (latitude: number, longitude: number): Promise<GeoValidateResponse> => {
  try {
    const response = await axios.post(`${API_BASE_URL}/geoValidate`, { latitude, longitude });
    return response.data;
  } catch (error: any) {
    if (error.response && error.response.data && error.response.status !== 401) {
      return error.response.data;
    }
    if (error.response?.status !== 401) {
        return {
            success: false,
            message: error.response?.data?.message || "Error de conexión con el servidor"
        };
    }
    return Promise.reject(error);
  }
};

// Función para abrir la puerta (original) - This might be deprecated if openDoorApi is the new one
export const openDoor = async (): Promise<OpenDoorResponse> => {
  try {
    const response = await axios.post(`${API_BASE_URL}/openDoor`);
    return response.data;
  } catch (error: any) {
    if (error.response && error.response.data && error.response.status !== 401) {
      return error.response.data;
    }
    if (error.response?.status !== 401) {
        return {
            success: false,
            message: error.response?.data?.message || "Error de conexión con el servidor"
        };
    }
    return Promise.reject(error);
  }
};

// Función para obtener el estado de la sesión
export const getSessionStatus = async (): Promise<SessionResponse> => {
  try {
    const response = await axios.get(`${API_BASE_URL}/session`);
    return response.data;
  } catch (error: any) {
    // For session status, a 401 is an expected state (not logged in), so don't necessarily throw generic error
    // The interceptor will catch 401 but we added a condition to not redirect for /api/session calls to prevent loops.
    if (error.response && error.response.status === 401) {
        return {
            validated: false,
            geoValidated: false,
            houseId: null
        };
    }
    // For other errors when fetching session
    return {
      validated: false,
      geoValidated: false,
      houseId: null
      // Potentially add an error message here if needed by UI
    };
  }
};

// Función para cerrar sesión
export const logout = async (): Promise<{ success: boolean }> => {
  try {
    const response = await axios.post(`${API_BASE_URL}/logout`);
    return response.data;
  } catch (error: any) {
     if (error.response?.status !== 401) {
        return {
            success: false
            // message: error.response?.data?.message || "Error during logout"
        };
    }
    // If 401 during logout, it means session was already gone. Consider it a success from client's perspective.
    return { success: true }; 
  }
};

// --- New API functions based on requirements, now using relative paths for proxying ---

// Function to get information from the new endpoint
export const getInfo = async (payload: GetInfoPayload): Promise<InfoData> => {
  // No try-catch needed here if we let the interceptor handle 401 and other errors propagate
  const response = await axios.post<InfoData>(`${API_BASE_URL}/proxy/getInfo`, payload, {
    headers: {
      "Content-Type": "application/json",
    },
  });
  return response.data;
};

// Function for the new open door API call - Updated to accept code, longitude, and latitude
export const openDoorApi = async (payload: OpenDoorApiPayload): Promise<OpenDoorApiResponse> => {
  // No try-catch needed here if we let the interceptor handle 401 and other errors propagate
  const response = await axios.post<OpenDoorApiResponse>(`${API_BASE_URL}/proxy/open`, payload, {
    headers: {
      "Content-Type": "application/json",
    },
  });
  return response.data;
};

// --- End of New API functions ---
